<template>
    <div>

        <Head title="Optional Accessories" />

        <div class="mb-8">
            <h1 class="text-3xl font-bold">Optional Accessories</h1>
        </div>

        <!-- Step indicator -->
        <div class="flex items-center justify-center mb-8">
            <div class="flex items-center">
                <div class="flex items-center">
                    <div :class="[
                        'rounded-full h-10 w-10 flex items-center justify-center',
                        currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                    ]">
                        1
                    </div>
                </div>

                <div class="w-32 h-1 mx-4" :class="currentStep >= 2 ? 'bg-blue-600' : 'bg-gray-300'"></div>

                <div class="flex items-center">
                    <div :class="[
                        'rounded-full h-10 w-10 flex items-center justify-center',
                        currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                    ]">
                        2
                    </div>
                </div>
            </div>
        </div>

        <!-- Step content -->
        <div class="bg-white shadow p-6">
            <!-- Step 1 -->
            <div v-show="currentStep === 1">
                <h2 class="text-xl font-semibold mb-6">Select Product Series</h2>
                <p class="text-gray-600 mb-8">Choose one of the following product series to continue:</p>

                <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
                    <button v-for="option in productOptions" :key="option" @click="selectProduct(option)" :class="[
                        'p-6 border-2 rounded-lg transition-all duration-200 font-medium text-lg',
                        selectedProduct === option
                            ? 'border-blue-600 bg-blue-50 text-blue-600'
                            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    ]">
                        {{ option }}
                    </button>
                </div>

                <div class="flex justify-end">
                    <button @click="nextStep" :disabled="!selectedProduct" :class="[
                        'px-6 py-2',
                        selectedProduct
                            ? 'btn-blue'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    ]">
                        Next
                    </button>
                </div>
            </div>

            <!-- Step 2 -->
            <div v-show="currentStep === 2">
                <h2 class="text-xl font-semibold mb-4">Select Accessories</h2>
                <p class="text-gray-600 mb-6">Selected product: <strong>{{ selectedProduct }}</strong></p>

                <!-- Loading state -->
                <div v-if="loadingAccessories" class="text-center py-10">
                    <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    <p class="mt-2 text-gray-600">Loading accessories...</p>
                </div>

                <!-- Accessories grid -->
                <div v-else-if="accessories.length > 0" class="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                    <div v-for="(accessory, idx) in accessories" :key="idx"
                        class="border border-solid p-4 hover:bg-gray-50 cursor-pointer relative"
                        :class="isAccessorySelected(accessory.id) ? 'border-blue-600 shadow-lg' : 'border-gray-200'"
                        @click="toggleAccessory(accessory)">

                        <!-- Image section -->
                        <div class="flex justify-center mb-3 h-24">
                            <img v-if="accessory.img_path" :src="getImageUrl(accessory.img_path)"
                                :alt="accessory.part_number" class="max-h-24 object-contain" />
                            <div v-else class="bg-gray-200 w-full h-24 flex items-center justify-center">
                                <span class="text-gray-400 text-xs">No image</span>
                            </div>
                        </div>

                        <!-- Selected checkmark -->
                        <div v-if="isAccessorySelected(accessory.id)" class="absolute top-3 right-3">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-600" viewBox="0 0 20 20"
                                fill="currentColor">
                                <path fill-rule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clip-rule="evenodd" />
                            </svg>
                        </div>

                        <!-- Part number and description -->
                        <div class="mb-4">
                            <div class="font-medium">{{ accessory.part_number }}</div>
                            <div class="text-gray-500 text-sm">{{ accessory.description || 'Accessory' }}</div>
                            <div v-if="accessory.width" class="text-gray-400 text-xs mt-1">Width: {{ accessory.width }}
                            </div>
                        </div>

                        <!-- Quantity section -->
                        <div class="mt-4">
                            <div v-if="isAccessorySelected(accessory.id)" class="flex items-center justify-between">
                                <span class="text-sm text-gray-500">Qty:</span>
                                <div class="flex items-center">
                                    <button type="button"
                                        class="border border-gray-300 rounded-l w-6 h-6 bg-gray-100 flex items-center justify-center text-sm"
                                        @click.stop="decrementQuantity(accessory.id)">
                                        -
                                    </button>
                                    <input type="number" min="1" :value="getAccessoryQuantity(accessory.id)"
                                        @change.stop="updateQuantity(accessory.id, $event.target.value)" @click.stop
                                        class="w-8 h-6 border-t border-b border-gray-300 text-center text-sm" />
                                    <button type="button"
                                        class="border border-gray-300 rounded-r w-6 h-6 bg-gray-100 flex items-center justify-center text-sm"
                                        @click.stop="incrementQuantity(accessory.id)">
                                        +
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- No accessories message -->
                <div v-else class="text-center p-10 bg-gray-50 rounded-md">
                    <p class="text-gray-600">No accessories available for {{ selectedProduct }}.</p>
                </div>

                <div class="flex justify-between">
                    <button @click="previousStep" class="border border-gray-400 px-6 py-2 hover:bg-gray-100">
                        Previous
                    </button>
                    <button @click="complete" class="btn-blue">
                        Complete
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import axios from 'axios'

export default {
    components: {
        Head,
        Link,
    },
    layout: Layout,
    data() {
        return {
            currentStep: 1,
            selectedProduct: null,
            productOptions: [
                'T1400',
                'T1460',
                'T2000',
                'T2060',
                'T3000',
                'T1500',
                'T1560',
                'T1700',
                'T1760',
                'T1800',
                'P6060',
                'P6080',
                'P6120'
            ],
            accessories: [],
            selectedAccessories: [],
            loadingAccessories: false
        }
    },
    methods: {
        selectProduct(product) {
            this.selectedProduct = product
        },

        async nextStep() {
            if (this.currentStep < 2 && this.selectedProduct) {
                this.currentStep++
                await this.loadAccessories()
            }
        },

        previousStep() {
            if (this.currentStep > 1) {
                this.currentStep--
            }
        },

        async loadAccessories() {
            this.loadingAccessories = true
            try {
                const response = await axios.get('/accessories/optional/accessories', {
                    params: { series: this.selectedProduct }
                })
                this.accessories = response.data.accessories || []
            } catch (error) {
                console.error('Error loading accessories:', error)
                this.accessories = []
            } finally {
                this.loadingAccessories = false
            }
        },

        getImageUrl(imgPath) {
            if (!imgPath) return null

            if (imgPath.startsWith('http://') || imgPath.startsWith('https://')) {
                return imgPath
            }

            if (imgPath.startsWith('/')) {
                return `${window.location.origin}${imgPath}?w=120&fit=contain`
            } else {
                return `${window.location.origin}/img/${imgPath}?w=120&fit=contain`
            }
        },

        isAccessorySelected(accessoryId) {
            return this.selectedAccessories.some(item => item.id === accessoryId)
        },

        getAccessoryQuantity(accessoryId) {
            const accessory = this.selectedAccessories.find(item => item.id === accessoryId)
            return accessory ? accessory.quantity : 1
        },

        toggleAccessory(accessory) {
            const index = this.selectedAccessories.findIndex(item => item.id === accessory.id)

            if (index > -1) {
                this.selectedAccessories.splice(index, 1)
            } else {
                this.selectedAccessories.push({
                    ...accessory,
                    quantity: 1
                })
            }
        },

        incrementQuantity(accessoryId) {
            const accessory = this.selectedAccessories.find(item => item.id === accessoryId)
            if (accessory) {
                accessory.quantity++
            }
        },

        decrementQuantity(accessoryId) {
            const accessory = this.selectedAccessories.find(item => item.id === accessoryId)
            if (accessory && accessory.quantity > 1) {
                accessory.quantity--
            }
        },

        updateQuantity(accessoryId, value) {
            const accessory = this.selectedAccessories.find(item => item.id === accessoryId)
            if (accessory) {
                accessory.quantity = parseInt(value, 10) || 1
            }
        },

        complete() {
            // Here you can add logic to save selected accessories
            console.log('Selected accessories:', this.selectedAccessories)
            this.$inertia.visit('/accessories')
        }
    }
}
</script>